import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { components } from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";
import { PlusIcon, X, User, CircleAlert, Mail } from "lucide-react";
import { search, getItem } from "../../api/ProjectApi";
import { teamOutboundRecipientsHistory } from "../../api/EmailApi";
import { timeAgo } from "../../ai-content/helpers";

export const EventHistory = ({ events }: { events: any }) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const [style, setStyle] = useState({});

  const triggerRef = useRef(null);
  const portalRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current || !portalRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const portalRect = portalRef.current.getBoundingClientRect();
    const showTop = triggerRect.top > portalRect.height + 12;
    const left = triggerRect.left + triggerRect.width / 2 + window.scrollX;
    const top = showTop
      ? triggerRect.top + window.scrollY - 8
      : triggerRect.bottom + window.scrollY + 8;
    setStyle({
      position: "absolute",
      zIndex: 9999,
      top,
      left,
      transform: showTop ? "translate(-50%, -100%)" : "translate(-50%, 0)",
    });
  };

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });
    if (portalRef.current) {
      resizeObserver.observe(portalRef.current);
    }
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || portalRef.current?.contains(target)) {
        return;
      }
      setVisible(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  return (
    <>
      <span
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setVisible(true);
        }}
        className="text-[10px] text-blue-600 shrink-0 cursor-pointer"
      >
        {t("See history")}
      </span>
      {visible &&
        createPortal(
          <div ref={portalRef} style={style} className="app-portal-root">
            <div className="p-2 max-w-120 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
              {events.map((event) => {
                const sentByDisplayName = event.get("sentByDisplayName");
                const sentTime = new Date(event.get("at") * 1000);
                return (
                  <div key={event.get("logId")}>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600 shrink-0" />
                      <div className="text-sm font-semibold text-gray-900">
                        {event.get("subject")}
                      </div>
                    </div>
                    <div className="pt-1 pl-6 text-xs text-gray-600">
                      {`${sentByDisplayName} ${t("emailed")} ${timeAgo(sentTime)}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

const MenuPortal = (props) => (
  <components.MenuPortal {...props} className="react-select-menu-portal" />
);

interface RecipientItem {
  id?: string; // item ID (for bulk contacts resolved from itemIds)
  value: string; // email
  label: string; // display name
  iconUrl?: string;
  source: "attendee" | "item";
}

interface Props {
  projectId: string;
  attendees: string[]; // manually-added email strings
  itemIds?: string; // comma-separated item IDs (from bulk select)
  onChangeAttendees: (attendees: string[]) => void;
  onChangeItemIds?: (itemIds: string) => void;
}

const RecipientsSidebar = ({
  projectId,
  attendees,
  itemIds,
  onChangeAttendees,
  onChangeItemIds,
}: Props) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const [resolvedAttendees, setResolvedAttendees] = useState<RecipientItem[]>([]);
  const [resolvedItemRecipients, setResolvedItemRecipients] = useState<RecipientItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Resolve attendee email strings → full contact objects (name + avatar)
  useEffect(() => {
    if (!attendees || attendees.length === 0) {
      setResolvedAttendees([]);
      return;
    }
    const currentEmail = currentUser.get("email");
    const currentItem: RecipientItem | null = attendees.includes(currentEmail)
      ? {
          value: currentEmail,
          label: currentUser.get("displayName") || currentEmail,
          iconUrl: currentUser.get("avatarUrl"),
          source: "attendee",
        }
      : null;

    const remaining = attendees.filter((e) => e !== currentEmail);
    if (remaining.length === 0) {
      setResolvedAttendees(currentItem ? [currentItem] : []);
      return;
    }

    search({
      projectId,
      pageSize: remaining.length + 5,
      types: ["CONTACT", "COMPANY"],
      filters: { email: { operator: "in", values: remaining } },
    })
      .then((result) => {
        const resolved: RecipientItem[] = fromJS(result)
          .getIn(["pageResult", "content"])
          .map((item) => ({
            id: item.get("id") as string,
            value: item.getIn(["data", "email", "value"]) as string,
            label: item.getIn(["data", "name", "value"]) as string,
            iconUrl: item.get("iconUrl") as string | undefined,
            source: "attendee" as const,
          }))
          .toJS();

        const resolvedMap = new Map(resolved.map((r) => [r.value, r]));
        const allResolved: RecipientItem[] = remaining.map(
          (email) =>
            resolvedMap.get(email) ?? {
              value: email,
              label: email,
              iconUrl: undefined,
              source: "attendee" as const,
            }
        );
        setResolvedAttendees(currentItem ? [currentItem, ...allResolved] : allResolved);
      })
      .catch(() => {
        // fallback: show raw emails
        const fallback: RecipientItem[] = remaining.map((email) => ({
          value: email,
          label: email,
          source: "attendee" as const,
        }));
        setResolvedAttendees(currentItem ? [currentItem, ...fallback] : fallback);
      });
  }, [attendees.length, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve itemIds (bulk contact IDs) → full contact objects via getItem
  useEffect(() => {
    if (!itemIds) {
      setResolvedItemRecipients([]);
      return;
    }
    const ids = itemIds.split(",").filter(Boolean);
    if (ids.length === 0) {
      setResolvedItemRecipients([]);
      return;
    }
    setLoadingItems(true);
    Promise.all(ids.map((id) => getItem(id)))
      .then((items) => {
        const resolved: RecipientItem[] = items
          .map((item) => {
            const js = fromJS(item);
            const email = js.getIn(["data", "email", "value"]) as string | undefined;
            const name = js.getIn(["data", "name", "value"]) as string | undefined;
            const iconUrl = js.get("iconUrl") as string | undefined;
            const id = js.get("id") as string;
            return {
              id,
              value: email || "",
              label: name || email || id,
              iconUrl,
              source: "item" as const,
            };
          })
          .filter((r) => r.value); // only contacts with email
        setResolvedItemRecipients(resolved);
      })
      .catch(() => {
        // fallback: show IDs only
        setResolvedItemRecipients(
          ids.map((id) => ({ id, value: id, label: id, source: "item" as const }))
        );
      })
      .finally(() => setLoadingItems(false));
  }, [itemIds]);

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      search({
        projectId,
        pageSize: 10,
        types: ["CONTACT", "COMPANY"],
        filters: { email: { operator: "regex", values: [inputValue] } },
      }).then((result) => {
        const datas = fromJS(result)
          .getIn(["pageResult", "content"])
          .filter((item) => !!item.getIn(["data", "email", "value"]))
          .map((item) => ({
            value: item.getIn(["data", "email", "value"]),
            label: item.getIn(["data", "name", "value"]),
            data: item,
          }))
          .toJS();
        callback(Array.from(new Map(datas.map((d) => [d.value, d])).values()));
      });
    },
    [projectId]
  );

  const handleRemoveAttendee = (email: string) => {
    onChangeAttendees(attendees.filter((a) => a !== email));
  };

  const handleRemoveItemId = (id: string) => {
    if (!onChangeItemIds) return;
    const ids = (itemIds || "")
      .split(",")
      .filter(Boolean)
      .filter((i) => i !== id);
    onChangeItemIds(ids.join(","));
  };

  // Combined list: bulk contacts first, then manually-added attendees
  const allRecipients: RecipientItem[] = [...resolvedItemRecipients, ...resolvedAttendees];

  const allEmails = allRecipients.map((item) => item.value);

  const { data: teamOutboundRecipientsData } = useQuery({
    queryKey: ["team-outbound-recipients-history", projectId, allEmails],
    queryFn: async () => {
      return await teamOutboundRecipientsHistory(projectId, {
        emails: allEmails,
      });
    },
    enabled: !!projectId && allEmails.length > 0,
    staleTime: 60 * 1000, // cache 60s
  });

  const teamOutboundRecipients = fromJS(teamOutboundRecipientsData?.results || []);

  const renderAvatar = (item: RecipientItem, size = "md") => {
    const sizeClass = size === "sm" ? "w-7 h-7 text-xs" : "w-8 h-8 text-sm";
    if (item.iconUrl) {
      return (
        <img
          src={item.iconUrl}
          alt={item.label}
          className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        />
      );
    }
    const initials = item.label
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    return (
      <div
        className={`${sizeClass} rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center flex-shrink-0`}
      >
        <span className="text-white font-semibold leading-none">
          {initials || <User className="w-3 h-3" />}
        </span>
      </div>
    );
  };

  return (
    <div className="flex flex-col self-stretch border-l border-gray-100 bg-gray-50 min-w-[220px] max-w-[280px] w-[260px]">
      {/* Add recipients */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        {!isPickerOpen ? (
          <button
            type="button"
            onClick={() => setIsPickerOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-400 text-sm hover:border-blue-400 hover:text-blue-500 transition-all cursor-pointer shadow-sm"
          >
            <PlusIcon className="w-4 h-4 flex-shrink-0" />
            <span>{t("Add recipients")}</span>
          </button>
        ) : (
          <div className="rounded-lg border-2 border-blue-500 shadow-sm bg-white">
            <AsyncCreatableSelect
              autoFocus
              menuPortalTarget={document.body}
              backspaceRemovesValue={false}
              components={{ DropdownIndicator: null, IndicatorSeparator: null, MenuPortal }}
              controlShouldRenderValue={false}
              tabSelectsValue={false}
              isClearable={false}
              menuIsOpen
              isMulti
              value={resolvedAttendees}
              onChange={(values) => {
                onChangeAttendees(values ? values.map((v: any) => v.value) : []);
                setIsPickerOpen(false);
              }}
              cacheOptions
              defaultOptions
              loadOptions={loadOptions}
              formatCreateLabel={(input) => `Add "${input}"`}
              isValidNewOption={(inputValue, _, selectOptions) => {
                if (!emailRegex.test(inputValue)) return false;
                return !selectOptions.some(
                  (opt) => opt.value?.toLowerCase() === inputValue.toLowerCase()
                );
              }}
              onCreateOption={(inputValue) => {
                if (!attendees.includes(inputValue)) {
                  onChangeAttendees([...attendees, inputValue]);
                }
                setIsPickerOpen(false);
              }}
              onBlur={() => setIsPickerOpen(false)}
              placeholder={t("Search or enter email...")}
              formatOptionLabel={(option: any) => {
                if (option["__isNew__"]) return option.label;
                const contact = fromJS(option.data || {});
                return (
                  <div className="flex items-center gap-2 py-0.5">
                    {renderAvatar(
                      {
                        value: option.value,
                        label: option.label,
                        iconUrl: contact.get("iconUrl"),
                        source: "attendee",
                      },
                      "sm"
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.value}</div>
                    </div>
                  </div>
                );
              }}
              styles={{
                control: (provided) => ({
                  ...provided,
                  border: "none",
                  boxShadow: "none",
                  minHeight: 38,
                  fontSize: 13,
                  backgroundColor: "transparent",
                }),
                menu: (provided) => ({
                  ...provided,
                  marginTop: 6,
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  border: "1px solid #e5e7eb",
                }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: 13,
                  backgroundColor: state.isSelected
                    ? "#dbeafe"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                }),
                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </div>
        )}
      </div>

      {/* Recipients list */}
      <div className="flex-1 overflow-y-auto py-1">
        {loadingItems && (
          <div className="px-4 py-6 flex items-center justify-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
            <span className="text-xs text-gray-400">{t("Loading...")}</span>
          </div>
        )}

        {!loadingItems &&
          allRecipients.map((item) => {
            const key = item.source === "item" ? item.id! : item.value;
            const teamOutboundRecipient =
              teamOutboundRecipients.find((recipient) => recipient.get("email") === item.value) ||
              fromJS({});
            const emailEvents = teamOutboundRecipient.get("events") || fromJS([]);
            const firstEvent = emailEvents.get(0) || fromJS({});
            const sentByDisplayName =
              item.value === firstEvent.get("sentByEmail")
                ? t("me")
                : firstEvent.get("sentByDisplayName");
            const sentTime = new Date(firstEvent.get("at") * 1000);
            const displayText = `${sentByDisplayName} ${t("emailed")} ${timeAgo(sentTime)}`;
            return (
              <div key={key} className="px-3 py-2.5 hover:bg-white/80 transition-colors">
                <div className="group flex items-center gap-3">
                  {renderAvatar(item, "md")}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate leading-tight">
                      {item.label}
                    </div>
                    {item.value && (
                      <div className="text-xs text-gray-400 truncate leading-tight mt-0.5">
                        {item.value}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.source === "item" && item.id) {
                        handleRemoveItemId(item.id);
                      } else {
                        handleRemoveAttendee(item.value);
                      }
                    }}
                  >
                    <X className="w-3 h-3 text-gray-500" />
                  </button>
                </div>
                {sentByDisplayName ? (
                  <div className="pt-1 flex items-center gap-2">
                    <CircleAlert className="w-4 h-4 text-red-500 shrink-0" />
                    <span title={displayText} className="text-[10px] text-gray-400 truncate">
                      {displayText}
                    </span>
                    <EventHistory events={emailEvents} />
                  </div>
                ) : null}
              </div>
            );
          })}

        {!loadingItems && allRecipients.length === 0 && (
          <div className="px-4 py-8 text-center">
            <div className="text-sm text-gray-400 mb-0.5">{t("No recipients yet")}</div>
            <div className="text-xs text-gray-300">{t("Click + Add recipients above")}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientsSidebar;
